import { Container, ContainerOptions, Ticker } from "pixi.js";

export type MarqueeMode = "scroll" | "sway";

export class Marquee extends Container {
  private items: Container[] = [];
  private continuous: boolean = true;
  private scrollSpeed: number = 10;
  private scrollWidth: number;
  private gap: number = 50;
  private direction: 1 | -1 = -1;
  private running: boolean = true;
  private _mode: MarqueeMode = "scroll";
  private _align: "left" | "center" = "left";
  private _startOffset: number = 0;
  private swayDelay: number = 0;
  private swayPauseRemaining: number = 0;

  constructor({
    width,
    speed = 100,
    gap = 50,
    mode = "scroll",
    align = "left" as "left" | "center",
    startOffset = 0,
    swayDelay = 0,
    containerOptions,
  }: {
    width: number;
    speed?: number;
    gap?: number;
    mode?: MarqueeMode;
    /** How to position content that is narrower than the marquee width. */
    align?: "left" | "center";
    /** Additional offset applied to the natural start position. */
    startOffset?: number;
    /** Milliseconds to pause at each end in sway mode. */
    swayDelay?: number;
    containerOptions?: ContainerOptions;
  }) {
    super(containerOptions);

    this.scrollWidth = width;
    this.scrollSpeed = speed;
    this.gap = gap;
    this._mode = mode;
    this._align = align;
    this._startOffset = startOffset;
    this.swayDelay = swayDelay;

    Ticker.shared.add(this.onTick);
  }

  get mode(): MarqueeMode {
    return this._mode;
  }

  set mode(value: MarqueeMode) {
    if (this._mode === value) return;
    this._mode = value;
    // Reset to the natural starting position when switching modes
    this.direction = -1;
    this.swayPauseRemaining = 0;
    this.layoutItems();
  }

  get align(): "left" | "center" {
    return this._align;
  }

  set align(value: "left" | "center") {
    if (this._align === value) return;
    this._align = value;
    this.layoutItems();
  }

  get startOffset(): number {
    return this._startOffset;
  }

  set startOffset(value: number) {
    if (this._startOffset === value) return;
    this._startOffset = value;
    this.layoutItems();
  }

  hasItem = (item: Container): boolean => {
    return (
      this.items.find((it) => {
        return it === item;
      }) !== undefined
    );
  };

  addItem = (
    item: Container,
    options?: {
      updateLayout?: boolean;
      unique?: boolean;
    },
  ): void => {
    if (options?.unique ?? false) this.removeItem(item);
    this.items.push(item);
    this.addChild(item);
    if (options?.updateLayout ?? true) this.layoutItems();
  };

  removeItem = (
    item: Container,
    options?: {
      updateLayout?: boolean;
    },
  ): void => {
    const index = this.items.indexOf(item);
    if (index > -1) {
      this.items.splice(index, 1);
      this.removeChild(item);
      if (options?.updateLayout ?? true) this.layoutItems();
    }
  };

  public clear = (): void => {
    while (this.items.length > 0) {
      this.removeItem(this.items[0], {
        updateLayout: false,
      });
    }
  };

  /**
   * Calculate total content width including gaps (no trailing gap).
   */
  private getTotalContentWidth(): number {
    if (this.items.length === 0) return 0;
    let total = 0;
    for (const item of this.items) {
      total += item.width;
    }
    total += this.gap * (this.items.length - 1);
    return total;
  }

  private getWidestItemWidth(): number {
    if (this.items.length === 0) return 0;
    let max = 0;
    for (const item of this.items) {
      if (item.width > max) max = item.width;
    }
    return max;
  }

  /**
   * Returns the natural home position for items[0], combining alignment and
   * startOffset. Centering only applies when content fits within the viewport.
   */
  private getHomeOffset(totalWidth: number): number {
    const alignOffset =
      this._align === "center" && totalWidth < this.scrollWidth
        ? (this.scrollWidth - totalWidth) / 2
        : 0;
    return alignOffset + this._startOffset;
  }

  /**
   * Layout items horizontally.
   * When called without an explicit offset, uses the home position derived
   * from alignment and startOffset. Explicit offsets are used as-is (internal
   * scroll/sway boundary snapping).
   */
  private layoutItems = (offset?: number): void => {
    const totalWidth = this.getTotalContentWidth();
    let start = offset ?? this.getHomeOffset(totalWidth);
    for (const item of this.items) {
      item.x = start;
      start += item.width + this.gap;
    }
  };

  public pause = (): void => {
    this.running = false;
  };

  public resume = (): void => {
    this.running = true;
  };

  public reset = (): void => {
    this.direction = -1;
    this.swayPauseRemaining = 0;
    this.layoutItems();
  };

  private onTick = (ticker: Ticker) => {
    if (this.items.length === 0 || !this.running) return;

    const totalWidth = this.getTotalContentWidth();

    // Nothing to scroll if all content fits within the visible width
    if (totalWidth <= this.scrollWidth) return;

    if (this._mode === "sway") {
      this.tickSway(ticker, totalWidth);
    } else {
      this.tickScroll(ticker, totalWidth);
    }
  };

  /**
   * Sway mode: treat all items as a single block and bounce between the two
   * extremes — left edge flush with the viewport start (offset 0) and right
   * edge flush with the viewport end (offset scrollWidth − totalWidth).
   */
  private tickSway = (ticker: Ticker, totalWidth: number): void => {
    const homeOffset = this.getHomeOffset(totalWidth);
    const minOffset = this.scrollWidth - totalWidth - this._startOffset;

    // Content fits within the inset area — nothing to sway
    if (minOffset >= homeOffset) return;

    // Pause at boundary if delay is configured
    if (this.swayPauseRemaining > 0) {
      this.swayPauseRemaining -= ticker.deltaMS;
      return;
    }

    const movement =
      this.scrollSpeed * (ticker.deltaTime / 60) * this.direction;
    const first = this.items[0];

    for (const item of this.items) {
      item.x += movement;
    }

    if (this.direction === -1 && first.x <= minOffset) {
      // Overshot the left boundary — clamp, pause, then reverse
      this.layoutItems(minOffset);
      this.swayPauseRemaining = this.swayDelay;
      this.direction = 1;
    } else if (this.direction === 1 && first.x >= homeOffset) {
      // Overshot the home boundary — clamp, pause, then reverse
      this.layoutItems(homeOffset);
      this.swayPauseRemaining = this.swayDelay;
      this.direction = -1;
    }
  };

  /**
   * Scroll mode: items continuously scroll in one direction; each item
   * wraps around to the opposite side when it leaves the viewport.
   */
  private tickScroll = (ticker: Ticker, totalWidth: number): void => {
    const widest = this.getWidestItemWidth();
    const movement =
      this.scrollSpeed * (ticker.deltaTime / 60) * this.direction;

    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      item.x += movement;

      if (this.continuous) {
        if (this.direction === -1 && item.x + item.width < 0) {
          // Place after the rightmost content, but never inside the viewport
          const maxX = Math.max(...this.items.map((it) => it.x + it.width));
          item.x = Math.max(maxX, this.scrollWidth) + this.gap;
        } else if (this.direction === 1 && item.x > this.scrollWidth) {
          // Place before the leftmost content, but never inside the viewport
          const minX = Math.min(...this.items.map((it) => it.x));
          item.x = Math.min(minX, 0) - item.width - this.gap;
        }
      }
    }

    if (
      !this.continuous ||
      totalWidth - widest - (this.items.length > 1 ? this.gap : 0) <
        this.scrollWidth
    ) {
      if (this.direction === -1) {
        const last = this.items[this.items.length - 1];
        if (last.x + last.width < 0) {
          this.layoutItems(this.scrollWidth);
        }
      } else {
        const first = this.items[0];
        if (first.x > this.scrollWidth) {
          this.layoutItems(-totalWidth);
        }
      }
    }
  };
}
